import { formatMoney, planAmountNetMinor } from './formatMoney'

export const ACCOUNT_LIMITS = { FREE: 2, STANDARD: 5, VIP: 10 } as const
export const BUNDLE_LIMITS = { FREE: 25, STANDARD: null, VIP: null } as const

export interface PlanRow {
  tier_code: string
  kind: string
  points_granted: number
  currency?: string
  amount_net_minor?: number
  amount_net_grosz?: number
  subscriber_amount_net_grosz?: number | null
}

export type CompareTier = 'FREE' | 'STANDARD' | 'VIP'

export function normalizeCompareTier(tierCode?: string | null): CompareTier {
  const code = (tierCode ?? 'FREE').toUpperCase()
  if (code === 'STANDARD' || code === 'VIP') return code
  return 'FREE'
}

export function canShowUpgradeCompare(tierCode: CompareTier): boolean {
  return tierCode === 'FREE' || tierCode === 'STANDARD'
}

export function upgradeCreditsPath(tierCode: CompareTier): string {
  return tierCode === 'STANDARD' ? '/credits?plan=VIP' : '/credits#plans'
}

export function computeTopupDiscountPercents(plans: PlanRow[]): number[] {
  return plans
    .filter(
      (p) =>
        p.kind === 'topup' &&
        p.subscriber_amount_net_grosz != null &&
        planAmountNetMinor(p) > 0,
    )
    .map((p) =>
      Math.round((1 - p.subscriber_amount_net_grosz! / planAmountNetMinor(p)) * 100),
    )
    .filter((pct) => pct > 0)
}

export function formatNetMoney(minor: number, currency: string, locale?: string): string {
  return formatMoney(minor, currency, locale)
}

/** `value` is already in major units (e.g. 0.42 for offer cost). */
export function formatOfferCost(value: number, currency: string, locale?: string): string {
  return formatMoney(Math.round(value * 100), currency, locale)
}

export function fullOfferPointCost(pricing: { action_key: string; cost_points: number }[]): number {
  const title =
    pricing.find((p) => p.action_key === 'title.v2.generate.success') ??
    pricing.find((p) => p.action_key === 'title.generate.success')
  const description = pricing.find((p) => p.action_key === 'description.generate.success')
  return (title?.cost_points ?? 2) + (description?.cost_points ?? 4)
}

function costPerFullOffer(priceNetMinor: number, pointsGranted: number, offerPointCost: number): number {
  if (pointsGranted <= 0) return 0
  return (priceNetMinor / 100 / pointsGranted) * offerPointCost
}

const PLAN_ECONOMICS_FALLBACK = {
  STANDARD: { priceNetMinor: 19_900, pointsGranted: 1000 },
  VIP: { priceNetMinor: 29_900, pointsGranted: 2500 },
} as const

function resolvePlanEconomics(
  plan: PlanRow | undefined,
  tier: 'STANDARD' | 'VIP',
  offerPointCost: number,
) {
  const fallback = PLAN_ECONOMICS_FALLBACK[tier]
  const priceNetMinor = plan ? planAmountNetMinor(plan) || fallback.priceNetMinor : fallback.priceNetMinor
  const pointsGranted = plan?.points_granted ?? fallback.pointsGranted
  return {
    priceNetMinor,
    pointsGranted,
    perOfferCost: costPerFullOffer(priceNetMinor, pointsGranted, offerPointCost),
  }
}

export interface PlanEconomicsDisplay {
  standardPrice: string
  standardPoints: number
  vipPrice: string
  vipPoints: number
  standardOfferCost: string
  vipOfferCost: string
  topupDiscount: string
  freeAccounts: string
  standardAccounts: string
  vipAccounts: string
  freeBundles: string
  standardBundles: string
  vipBundles: string
}

export function buildPlanEconomicsDisplay(
  plans: PlanRow[],
  featurePricing: { action_key: string; cost_points: number }[],
  t: (key: string, opts?: Record<string, unknown>) => string,
  locale?: string,
): { economics: PlanEconomicsDisplay; offerPointCost: number } {
  const standardPlan = plans.find((p) => p.tier_code === 'STANDARD' && p.kind === 'subscription')
  const vipPlan = plans.find((p) => p.tier_code === 'VIP' && p.kind === 'subscription')
  const currency =
    standardPlan?.currency ?? vipPlan?.currency ?? plans.find((p) => p.currency)?.currency ?? 'PLN'
  const offerPointCost = fullOfferPointCost(featurePricing)
  const standardEconomics = resolvePlanEconomics(standardPlan, 'STANDARD', offerPointCost)
  const vipEconomics = resolvePlanEconomics(vipPlan, 'VIP', offerPointCost)

  const percents = computeTopupDiscountPercents(plans)
  let topupDiscount: string
  if (percents.length === 0) {
    topupDiscount = t('upgradeTopBar.values.topupDiscountFallback')
  } else {
    const min = Math.min(...percents)
    const max = Math.max(...percents)
    topupDiscount =
      min === max
        ? t('upgradeTopBar.values.topupDiscountSingle', { percent: min })
        : t('upgradeTopBar.values.topupDiscountRange', { min, max })
  }

  const formatAccounts = (count: number) => t('planCard.maxAccounts', { count })
  const formatBundles = (limit: number | null) =>
    limit == null ? t('upgradeTopBar.unlimited') : t('upgradeTopBar.bundleActions', { count: limit })

  return {
    offerPointCost,
    economics: {
      standardPrice: formatNetMoney(standardEconomics.priceNetMinor, currency, locale),
      standardPoints: standardEconomics.pointsGranted,
      vipPrice: formatNetMoney(vipEconomics.priceNetMinor, currency, locale),
      vipPoints: vipEconomics.pointsGranted,
      standardOfferCost: formatOfferCost(standardEconomics.perOfferCost, currency, locale),
      vipOfferCost: formatOfferCost(vipEconomics.perOfferCost, currency, locale),
      topupDiscount,
      freeAccounts: formatAccounts(ACCOUNT_LIMITS.FREE),
      standardAccounts: formatAccounts(ACCOUNT_LIMITS.STANDARD),
      vipAccounts: formatAccounts(ACCOUNT_LIMITS.VIP),
      freeBundles: formatBundles(BUNDLE_LIMITS.FREE),
      standardBundles: formatBundles(BUNDLE_LIMITS.STANDARD),
      vipBundles: formatBundles(BUNDLE_LIMITS.VIP),
    },
  }
}
